import { z } from "zod"

import type { ResolvedGalleryItem } from "./gallery"
import type { StoreConfig } from "./types"

/** BMS caps an announcement's title at 60 characters; a longer one is cut, not dropped. */
export const ANNOUNCEMENT_TITLE_MAX_LENGTH = 60
/** BMS caps an announcement's text at 1000 characters; a longer one is cut, not dropped. */
export const ANNOUNCEMENT_TEXT_MAX_LENGTH = 1000
/** A media caption is capped like a gallery caption (140); a longer one is cut. */
export const ANNOUNCEMENT_MEDIA_CAPTION_MAX_LENGTH = 140

/**
 * One colour set for an announcement panel, each a lowercase `#rrggbb` (or
 * `#rgb`). `null` ⇒ the storefront's theme colour for that slot.
 */
export interface AnnouncementColorSet {
  background: string | null
  title: string | null
  text: string | null
  buttonBackground: string | null
  buttonText: string | null
}

/** An announcement ready to render as plain text — never HTML. */
export interface ResolvedAnnouncement {
  /** Stable across edits and reorders: the React key. */
  id: string
  /** `null` ⇒ no heading; the text stands alone. */
  title: string | null
  /**
   * The text split on blank lines, in order and never empty. A single line
   * break inside a paragraph is kept (render with `white-space: pre-line`).
   */
  paragraphs: string[]
  /** Photo or video beside the text. `null` ⇒ none. */
  media: ResolvedGalleryItem | null
  /** Photo or video behind the panel. `null` ⇒ the background colour alone. */
  background: ResolvedGalleryItem | null
  /**
   * Custom colours for light and dark mode. Each dark slot already falls back
   * to the light one, so a panel designed once keeps its look in both modes.
   */
  colors: { light: AnnouncementColorSet; dark: AnnouncementColorSet }
}

/** `StoreConfig.announcements` narrowed to what the storefront shows. */
export interface ResolvedAnnouncements {
  /** Active announcements in staff display order. */
  items: ResolvedAnnouncement[]
}

/**
 * Split announcement text into paragraphs on blank lines. Line endings are
 * normalised, each paragraph is trimmed (single line breaks inside it stay),
 * and empty ones are dropped.
 */
export function announcementParagraphs(text: string): string[] {
  return text
    .replace(/\r\n?/g, "\n")
    .split(/\n[ \t]*\n/)
    .map((paragraph) =>
      paragraph
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean)
        .join("\n")
    )
    .filter(Boolean)
}

// Same rule as a gallery entry (`resolveGallery`), restated rather than
// imported: model modules carry no sibling runtime imports, which keeps each
// one runnable under plain `node --test`.
const mediaSchema = z
  .object({
    url: z.url({ protocol: /^https?$/ }),
    type: z.unknown().optional(),
    caption: z.unknown().optional(),
  })
  .transform(
    ({ url, type, caption }): ResolvedGalleryItem => ({
      url,
      type: type === "video" ? "video" : "image",
      caption:
        typeof caption === "string" && caption.trim()
          ? caption.trim().slice(0, ANNOUNCEMENT_MEDIA_CAPTION_MAX_LENGTH)
          : null,
    })
  )

const media = (raw: unknown) => mediaSchema.safeParse(raw).data ?? null

/**
 * Colours land in inline styles, so only a strict hex survives — never a
 * keyword, function or anything that could carry other CSS.
 */
const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i

function color(raw: unknown): string | null {
  return typeof raw === "string" && HEX_COLOR.test(raw.trim())
    ? raw.trim().toLowerCase()
    : null
}

const COLOR_SLOTS = [
  "background",
  "title",
  "text",
  "buttonBackground",
  "buttonText",
] as const

function colorSet(
  raw: unknown,
  fallback?: AnnouncementColorSet
): AnnouncementColorSet {
  const source =
    raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {}
  const set = {} as AnnouncementColorSet
  for (const slot of COLOR_SLOTS) {
    set[slot] = color(source[slot]) ?? fallback?.[slot] ?? null
  }
  return set
}

const announcementSchema = z
  .object({
    id: z.string().trim().min(1),
    title: z.unknown().optional(),
    text: z.string(),
    isActive: z.unknown().optional(),
    media: z.unknown().optional(),
    background: z.unknown().optional(),
    colors: z.unknown().optional(),
    darkColors: z.unknown().optional(),
  })
  .transform((raw): ResolvedAnnouncement => {
    const heading =
      typeof raw.title === "string"
        ? raw.title.trim().slice(0, ANNOUNCEMENT_TITLE_MAX_LENGTH)
        : ""
    const light = colorSet(raw.colors)
    return {
      id: raw.id,
      title: heading || null,
      paragraphs: announcementParagraphs(
        raw.text.trim().slice(0, ANNOUNCEMENT_TEXT_MAX_LENGTH)
      ),
      media: media(raw.media),
      background: media(raw.background),
      colors: { light, dark: colorSet(raw.darkColors, light) },
    }
  })

/**
 * Narrow a tenant's raw `announcements` for the home-page section:
 *   - null/absent (never configured) ⇒ no announcements; the deprecated
 *     `showAutoMessages` is ignored (the switch lives on `promotions`);
 *   - an item whose `isActive` is exactly `false` never shows (absent reads as
 *     active, like BMS's default); the rest keep staff order;
 *   - an item without an id or with no text left after trimming is dropped, as
 *     is a second item reusing an id (it would collide as a React key);
 *   - blank titles read as absent; title/text past 60/1000 characters are cut;
 *   - `media` and `background` are narrowed like a gallery entry: an `http(s)`
 *     URL is required, a `type` other than `video` is a photo, a blank caption
 *     reads as absent and one past 140 characters is cut; unusable media is
 *     dropped, never the announcement;
 *   - each colour must be a hex (`#rgb` / `#rrggbb`), else it reads as unset;
 *     an unset dark colour takes the light one, an unset light one the theme.
 */
export function resolveAnnouncements(
  config: Pick<StoreConfig, "announcements">
): ResolvedAnnouncements {
  const announcements = config.announcements
  const raw = Array.isArray(announcements?.items) ? announcements.items : []
  const seen = new Set<string>()
  const items: ResolvedAnnouncement[] = []
  for (const item of raw) {
    if (item?.isActive === false) continue
    const parsed = announcementSchema.safeParse(item)
    if (
      !parsed.success ||
      parsed.data.paragraphs.length === 0 ||
      seen.has(parsed.data.id)
    )
      continue
    seen.add(parsed.data.id)
    items.push(parsed.data)
  }
  return { items }
}
